// dependencies
import moment from "moment";
import { useDispatch } from "react-redux";
import { GenerateProcess } from "redux-process";
import { useImmer } from "use-immer";

// utils
import get from "../../utils/GetNested";

const useCachedDataFetcher = () => {
	/**
	 * We create a state object that serves as a cache. When parsing
	 * values that need to be fetched over the network, we will check to see
	 * if the value already exists in the cache before sending the request.
	 */
	const [cache, updateCache] = useImmer({
		employees: {},
		goals: {},
		goalOutlooks: {},
		goalCategories: {},
	});

	const dispatch = useDispatch();

	// functions to fetch api data
	const fetchEmployeeName = async (userID) => {
		if (userID) {
			const userData = await dispatch(GenerateProcess("GetUserInformation", { userID }));
			const userName = userData?.response ? userData.response.get("name") : "";
			return { name: userName, id: userID };
		}
	};

	const fetchGoalData = async (goalID) => {
		const goalData = await dispatch(GenerateProcess("GetGoal", { goalID }));

		const result = goalData.response.get("goal");

		const formattedData = {
			color: result.getIn(["outlook", "color"]),
			value: result.get("id"),
			label: result.get("name") || "GOAL WITHOUT A NAME",
			status: result.get("status"),
		};
		return formattedData;
	};

	const fetchGoalOutlookData = async (colorID) => {
		const goalOutlookData = await dispatch(GenerateProcess("getGoalOutlook", { color_id: colorID }));
		return goalOutlookData.response;
	};

	const fetchGoalCategoryData = async (goalCategoryID) => {
		const goalCategoryData = await dispatch(GenerateProcess("getGoalCategory", { category_id: goalCategoryID }));

		return goalCategoryData.response;
	};

	const getOrFetchFullValue = async (value, type) => {
		switch (type) {
			case "date": {
				return moment(value[0]);
			}

			case "userID": {
				const emplyeeID = value[0];
				let cachedEmployee = get(cache, ["employees", emplyeeID], null);

				if (!cachedEmployee) {
					const employeeData = await fetchEmployeeName(emplyeeID);

					updateCache((draft) => {
						draft.employees[emplyeeID] = employeeData;
					});

					cachedEmployee = employeeData;
				}
				return cachedEmployee;
			}

			// returns array of user objects
			case "userIDs": {
				const pendingUserData = value.map(async (userID) => {
					let cachedEmployee = get(cache, ["employees", userID], null);

					if (!cachedEmployee) {
						const employeeName = await fetchEmployeeName(userID);

						updateCache((draft) => {
							draft.employees[userID] = employeeName;
						});

						cachedEmployee = employeeName;
					}
					return cachedEmployee;
				});

				const users = Promise.all(pendingUserData);

				return users;
			}

			case "goalCategory": {
				const pendingGoalCategoryData = value.map(async (categoryID) => {
					let cachedGoalCategory = get(cache, ["goalCategories", categoryID], null);

					if (!cachedGoalCategory) {
						const goalCategoryData = await fetchGoalCategoryData(categoryID);

						updateCache((draft) => {
							draft.goalCategories[categoryID] = goalCategoryData;
						});

						cachedGoalCategory = goalCategoryData;
					}
					return cachedGoalCategory;
				});

				const goalCategories = await Promise.all(pendingGoalCategoryData);

				return { input: "", pool: goalCategories };
			}

			case "goalOutlook": {
				const pendingGoalOutlookData = value.map(async (outlookID) => {
					let cachedGoalOutlook = get(cache, ["goalOutlooks", outlookID], null);

					if (!cachedGoalOutlook) {
						const goalOutlookData = await fetchGoalOutlookData(outlookID);

						updateCache((draft) => {
							draft.goalOutlooks[outlookID] = goalOutlookData;
						});

						cachedGoalOutlook = goalOutlookData;
					}
					return cachedGoalOutlook;
				});
				const goalOutlooks = await Promise.all(pendingGoalOutlookData);

				return { input: "", pool: goalOutlooks };
			}

			case "multiSelect": {
				return value;
			}

			case "checkbox": {
				const isTrue = value[0] === "true";
				return isTrue;
			}

			case "goalPicker": {
				const goalID = value[0];
				let cachedGoal = get(cache, ["goals", goalID], null);

				if (!cachedGoal) {
					const goalData = await fetchGoalData(goalID);

					updateCache((draft) => {
						draft.goals[goalID] = goalData;
					});

					cachedGoal = goalData;
				}
				return cachedGoal;
			}

			case "formPicker": {
				return {
					id: value[0],
					option: null,
					name: value[0],
				};
			}

			default: {
				return value[0];
			}
		}
	};

	return {
		getFullValue: getOrFetchFullValue,
	};
};

export default useCachedDataFetcher;
